'use client';

import { useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type ShuffleMode = 'lines' | 'words' | 'sentences';

function fisherYates<T>(items: T[]): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function splitInput(input: string, mode: ShuffleMode): string[] {
  if (mode === 'words') {
    return input.split(/\s+/);
  }
  if (mode === 'sentences') {
    const matches = input.match(/[^.!?]+[.!?]*(\s+|$)/g);
    return matches ? matches.map((s) => s.trim()) : [];
  }
  return input.split(/\r?\n/);
}

function joinOutput(items: string[], mode: ShuffleMode): string {
  if (mode === 'words') {
    return items.join(' ');
  }
  if (mode === 'sentences') {
    return items.join(' ');
  }
  return items.join('\n');
}

export default function BrowserShuffleLines() {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<ShuffleMode>('lines');
  const [trimBlanks, setTrimBlanks] = useState(true);
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [output, setOutput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleShuffle() {
    setCopyMessage(null);
    if (!input.trim()) {
      setErrorMessage('Paste some text to shuffle first.');
      setOutput('');
      return;
    }
    setErrorMessage(null);

    let items = splitInput(input, mode).map((item) => (mode === 'lines' ? item : item.trim()));
    if (trimBlanks) {
      items = items.filter((item) => item.trim().length > 0);
    }
    if (removeDuplicates) {
      const seen = new Set<string>();
      items = items.filter((item) => {
        const key = item.trim();
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    }

    if (items.length === 0) {
      setErrorMessage('No items left to shuffle after filtering.');
      setOutput('');
      return;
    }

    setOutput(joinOutput(fisherYates(items), mode));
  }

  async function handleCopy() {
    if (!output) {
      return;
    }
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the output.');
    }
  }

  const unitLabel = mode === 'words' ? 'words' : mode === 'sentences' ? 'sentences' : 'lines';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Shuffle</span>
        <h2 className="tool-live-title">Shuffle lines, words, or sentences</h2>
        <p className="tool-live-subtitle">
          Paste a list and randomly reorder it with an unbiased Fisher-Yates shuffle. Optionally
          drop blanks and duplicates. Runs fully in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Input text</span>
            <textarea
              onChange={(e) => setInput(e.target.value)}
              placeholder={'One item per line…\nParis\nTokyo\nBerlin'}
              rows={12}
              value={input}
            />
          </label>
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Shuffle by</span>
              <select onChange={(e) => setMode(e.target.value as ShuffleMode)} value={mode}>
                <option value="lines">Lines</option>
                <option value="words">Words</option>
                <option value="sentences">Sentences</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Trim blank {unitLabel}</span>
              <select
                onChange={(e) => setTrimBlanks(e.target.value === 'yes')}
                value={trimBlanks ? 'yes' : 'no'}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Remove duplicates</span>
              <select
                onChange={(e) => setRemoveDuplicates(e.target.value === 'yes')}
                value={removeDuplicates ? 'yes' : 'no'}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleShuffle} type="button">
              Shuffle
            </button>
            <button
              className="button button-secondary"
              disabled={!output}
              onClick={handleCopy}
              type="button"
            >
              Copy result
            </button>
          </div>
          {errorMessage ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {errorMessage}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Shuffled output</span>
            <textarea
              placeholder="Press Shuffle to randomly reorder your text."
              readOnly
              rows={16}
              value={output}
            />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
